'use client';
import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';

import { ArrowUp, FileSpreadsheet, FileUp, RotateCw, BarChart3, Brain, FileBarChart2, BarChart } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { toast } from '@/hooks/use-toast';
import { sampleSalesData } from '@/data/sampleData';

interface ChatFile {
  id: string;
  filename: string;
}

export function ChatTeaser() {
  const [message, setMessage] = useState('');

  const router = useRouter();
  const { data: session, status } = useSession();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (status === 'unauthenticated') {
      router.push('/auth/signin');
      return;
    }

    if (!message.trim()) return;

    try {
      // Create new chat
      const response = await fetch('/api/chat/add-chats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: message.length > 40 ? message.substring(0, 37) + '...' : message,
          userId: session?.user?.id,
        }),
      });

      const data = await response.json();
      if (data.chat?.id) {
        router.push(`/chat/${data.chat.id}?message=${encodeURIComponent(message)}`);
      }
    } catch (error) {
      console.error('Error creating new chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to create new chat. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setMessage(suggestion);

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const suggestions = [
    // "Analyze sales data",
    'Create data visualization',
    'Predict customer trends',
    'Generate report',
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col items-center">
      {/* Heart Logo */}
      {/* <div className="relative w-32 h-32 mb-12">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-400 rounded-full blur-sm"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <BarChart className="w-24 h-24 text-white" strokeWidth={2} />
      </div>
    </div> */}

      <div className="relative w-32 h-32 mb-12">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-400 rounded-full blur-sm"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-24 h-24 stroke-white fill-none" strokeWidth="2">
            {/* Simple upward trend line with dot markers */}
            <path d="M4,16 L9,11 L14,13 L20,7" className="stroke-white" strokeLinecap="round" strokeLinejoin="round" />

            {/* Dot markers at each data point */}
            <circle cx="4" cy="16" r="1.5" className="fill-white" />
            <circle cx="9" cy="11" r="1.5" className="fill-white" />
            <circle cx="14" cy="13" r="1.5" className="fill-white" />
            <circle cx="20" cy="7" r="1.5" className="fill-white" />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <motion.h1
        className="text-5xl md:text-6xl font-bold text-white text-center mb-6"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.6 }}>
        Data Insights in Seconds
      </motion.h1>

      {/* Subheading */}
      <motion.p
        className="text-xl text-slate-300 text-center mb-10"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.6, delay: 0.2 }}>
        Chat with your data and get expert-level insights instantly.
      </motion.p>

      {/* Input Box */}
      <div className="w-full bg-slate-800/60 backdrop-blur-sm border border-slate-700 rounded-lg overflow-hidden mb-8 shadow-2xl relative">
        <div className="absolute -inset-1 bg-blue-500 opacity-20 blur-xl rounded-full animate-pulse pointer-events-none"></div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 relative">
            {/* Add a changing input text component in here */}
            <input
              type="text"
              value={message}
              placeholder="Ask me anything about your data..."
              className="w-full bg-transparent text-white outline-none text-lg placeholder-slate-400"
              ref={inputRef}
              onChange={e => setMessage(e.target.value)}
            />
          </div>
        </form>

        <div className="flex items-center justify-between border-t border-slate-700 p-3">
          <div className="flex items-center gap-4">
            <button
              className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"
              onClick={() => router.push('/chat')}>
              <FileSpreadsheet className="h-5 w-5" />
              <span>Use Sample Data</span>
            </button>
            <button
              className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"
              onClick={() => router.push('/chat')}>
              <FileUp className="w-5 h-5" />
              <span>Upload Data</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button className="bg-blue-600 hover:bg-blue-700 rounded-full p-1 transition-colors" onClick={handleSubmit}>
              <ArrowUp className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Suggestion Buttons */}
      <div className="flex flex-wrap justify-start gap-1">
        {suggestions.map((suggestion, index) => (
          <motion.button
            key={index}
            type="button"
            className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 
            text-white rounded-full transition-all duration-200 
            border border-white/20 hover:border-white/30
            shadow-lg hover:shadow-xl backdrop-blur-sm
            transform hover:scale-105"
            onClick={() => handleSuggestionClick(suggestion)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}>
            {index === 0 && <BarChart3 className="w-4 h-4" />}
            {index === 1 && <FileBarChart2 className="w-4 h-4" />}
            {index === 2 && <Brain className="w-4 h-4" />}
            {index === 3 && <RotateCw className="w-4 h-4" />}
            <span>{suggestion}</span>
            <ArrowUp className="w-4 h-4" />
          </motion.button>
        ))}
      </div>
    </div>
  );
}
